<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="component"></column-header>
    <div class="tabulator-parent collection-editor">
      <div class="row">
        <div class="col-md-12">
          <h3 v-if="collection.name">{{ collection.name }}</h3>
          <h3 v-else>{{ _('New collection') }}</h3>
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-lg-6 leftcol">
                <div class="form-group languageversioned">
                  <label for="name">{{ _('Name') }}</label>
                  <input type="text" class="form-control" id="name" v-model="collection.name" :placeholder="_('Collection name')" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 leftcol">
                <div class="form-group languageversioned">
                  <label for="canonical">{{ _('Direct URL') }}</label>
                  <div class="input-group">
                    <span class="input-group-addon">collection-</span>
                    <input type="text" class="form-control" id="canonical" :placeholder="_('Enter a direct URL path fragment that can be used to access this collection')" name="canonical" v-model="collection.canonical" />
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <label class="hidden-xs"></label>
                <p>
                  {{ _('You can set a custom URL path fragment that is used in all the links to this item in the Storefront. It may also be displayed in the browser\'s location bar. If you don\'t specify one, it will be automatically generated for you.') }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group languageversioned">
                  <label for="description">{{ _('Description') }}</label>
                  <editor v-model="collection.description" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label>{{ _('Front page product') }}</label>
                  <v-select :options="allProducts" v-model="selectedFrontpageProduct" label="name" keys="['id', 'name']" :placeholder="_('Type to search products')" :create-item="false" :clearable="true">
                    <template v-slot:no-options="{ searching }">
                      <template v-if="searching">
                        {{ _('No results found.') }}
                      </template>
                      <template v-else>{{ _('Type to search products') }}</template>
                    </template>
                  </v-select>
                  <p class="help-block">{{ _('If empty, the storefront shows an automatic front page with the collection name, description, and categories.') }}</p>
                </div>
              </div>
            </div>
            <div class="row" v-if="accessProducts.length">
              <div class="col-md-12">
                <div class="form-group">
                  <label>{{ _('Products that grant access') }}</label>
                  <div class="list-group">
                    <a
                      v-for="product in accessProducts"
                      :key="product.id"
                      class="list-group-item"
                      href="javascript:;"
                      v-on:click="openAccessProduct(product)"
                    >
                      <h4 class="list-group-item-heading">{{ product.name }}</h4>
                    </a>
                  </div>
                  <p class="help-block">{{ _('Customers who purchase or reserve these products are added to a group that can access this collection.') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('categories')" class="tab-pane active" id="categories" v-if="isTabActive('categories')" key="categories">
            <div class="row">
              <div class="col-md-12">
                <h4>{{ _('Categories in this collection') }}</h4>
                <draggable-multi-selector v-model="collection.sharedCategory" :items="allCategories" itemProperty="name" :selectorTitle="_('Add a category')" />
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('members')" class="tab-pane active" id="members" v-if="isTabActive('members')" key="members">
            <div class="row">
              <div class="col-md-12">
                <CollectionMemberList
                  :key="'collection-members-' + (collection.id || 'new')"
                  ref="members"
                  :details-trail="componentTrail"
                  :group-id="linkedGroupId"
                  :collection-id="advancedCollections ? collection.id : null"
                  :groups="memberGroups"
                  :show-group-column="!!advancedCollections"
                  :context-label="collection.name || _('New collection')"
                  :pending-invites="pendingInvites"
                  :empty-hint="membersEmptyHint"
                  :has-all-users-access="hasAllUsersAccess"
                  :read-only="!!membersReadOnly"
                  v-on:queued="queueInvite"
                  v-on:remove-pending="removePendingInvite"
                />
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('permissions')" class="tab-pane active" id="permissions" v-if="isTabActive('permissions') && advancedCollections" key="permissions">
            <div class="row">
              <div class="col-md-12">
                <h4>{{ _('User groups with access to this collection') }}</h4>
                <draggable-multi-selector v-model="collection.sharedUsergroup" :items="allGroups" itemProperty="name" :selectorTitle="_('Add a user group')" :sort="true" />
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-if="collection.id" v-on:click="remove" :disabled="saving"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import memberInvites from '~/lib/memberInvites'
import tabs from '~/lib/tabs'
import CollectionMemberList from '~/components/CollectionMemberList'
import { DraggableMultiSelector, Editor } from '#components'

export default {
  mixins: [changewatcher, components, memberInvites, tabs],
  components: { CollectionMemberList, DraggableMultiSelector, Editor },
  data () {
    return {
      collection: {
        name: '',
        canonical: '',
        description: '',
        frontpageproductid: null,
        sharedCategory: [],
        sharedUsergroup: [],
        accessproducts: []
      },
      allCategories: [],
      allProducts: [],
      allGroups: []
    }
  },
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'categories', label: this._('Categories') },
        { id: 'members', label: this._('Members') },
        { id: 'permissions', label: this._('Permissions'), hidden: !this.advancedCollections }
      ])
    },
    advancedCollections () {
      return !!parseInt(this.$store.getters.advancedCollections)
    },
    memberGroups () {
      return this.asItemList(this.collection.sharedUsergroup).map(group => {
        if (group && typeof group === 'object') return group
        return this.allGroups.find(g => parseInt(g.id) === parseInt(group)) || { id: group, name: group }
      })
    },
    editableMemberGroups () {
      return this.memberGroups.filter(group => group && !parseInt(group.allusers))
    },
    linkedGroupId () {
      if (this.advancedCollections || !this.editableMemberGroups.length) {
        return null
      }
      return this.editableMemberGroups[0].id || null
    },
    accessProducts () {
      return this.collection.accessproducts || []
    },
    hasAllUsersAccess () {
      return this.memberGroups.some(group => parseInt(group && group.allusers))
    },
    membersEmptyHint () {
      if (!this.collection.id) {
        return this._('Save the collection first to add existing users. Invitations are sent when you save.')
      }
      if (!this.editableMemberGroups.length && !this.hasAllUsersAccess) {
        return this._('Assign at least one user group on the Permissions tab before adding members.')
      }
      return ''
    },
    membersReadOnly () {
      return false
    },
    validatedActiveTab () {
      const visibleTabs = this.advancedCollections
        ? ['general', 'categories', 'members', 'permissions']
        : ['general', 'categories', 'members']
      if (visibleTabs.includes(this.activeTab)) {
        return this.activeTab
      }
      if (this.activeTab === 'permissions' && !this.advancedCollections) {
        return 'members'
      }
      return 'general'
    },
    selectedFrontpageProduct: {
      get () {
        if (!this.collection.frontpageproductid) {
          return null
        }
        return this.allProducts.find(product => parseInt(product.id) === parseInt(this.collection.frontpageproductid)) || null
      },
      set (product) {
        this.collection.frontpageproductid = product ? product.id : null
      }
    }
  },
  watch: {
    advancedCollections (newValue) {
      this.ensureValidActiveTab()
      if (newValue && !this.allGroups.length) {
        this.loadGroups()
      }
    }
  },
  methods: {
    asItemList (value) {
      if (Array.isArray(value)) {
        return value.filter(item => item != null)
      }
      if (value && typeof value === 'object') {
        if (value.id != null) {
          return [value]
        }
        return Object.values(value).filter(item => item && typeof item === 'object' && item.id != null)
      }
      return []
    },
    ensureValidActiveTab () {
      const validTab = this.validatedActiveTab
      if (validTab !== this.activeTab) {
        this.tab(validTab)
      }
    },
    resetCollection () {
      this.collection = {
        name: '',
        canonical: '',
        description: '',
        frontpageproductid: null,
        sharedCategory: [],
        sharedUsergroup: [],
        accessproducts: []
      }
      this.pendingInvites = []
      this.tab('general')
      this.storeState()
    },
    async reloadOnVersionChange () {
      if (!this.collection || !this.collection.id) {
        return
      }
      await this.loadCollection(this.collection.id)
    },
    async loadCollection (id) {
      if (!id) {
        return
      }
      this.loading = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/collections/' + id + '.json?apikey=' + this.apiKey + this.versionStr)
        this.collection = response.data
        this.collection.sharedCategory = this.asItemList(this.collection.sharedCategory)
        this.collection.sharedUsergroup = this.asItemList(this.collection.sharedUsergroup)
        if (!this.collection.accessproducts) this.collection.accessproducts = []
        this.collection.sharedUsergroup.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } catch (e) {
        this.showError(e)
      }
      this.storeState()
      this.loading = false
    },
    async loadCategories () {
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/categories.json?simple=true&apikey=' + this.apiKey)
        this.allCategories = (response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } catch (e) {}
    },
    async loadProducts () {
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/products.json?simple=true&iSortingCols=1&iSortCol_0=2&sSortDir_0=asc&apikey=' + this.apiKey)
        this.allProducts = (response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } catch (e) {}
    },
    async loadGroups () {
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/usergroups.json?apikey=' + this.apiKey)
        this.allGroups = (response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } catch (e) {}
    },
    getInviteGroupId () {
      return this.linkedGroupId
    },
    getInviteMembers () {
      return []
    },
    openAccessProduct (product) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ProductDetails',
        title: this._('Product') + ' ' + product.id + ': ' + product.name,
        width: 6,
        data: {
          productId: product.id,
          trail: this.componentTrail
        }
      })
    },
    getInviteContextLabel () {
      return this.collection.name || this._('New collection')
    },
    getSaveData () {
      const data = {
        id: this.collection.id || null,
        name: this.collection.name || '',
        canonical: this.collection.canonical || '',
        description: this.collection.description || '',
        frontpageproductid: this.collection.frontpageproductid || null,
        sharedCategory: (this.collection.sharedCategory || []).map(c => c.id || c),
        sharedUsergroup: (this.collection.sharedUsergroup || []).map(g => g.id || g)
      }
      data.version = this.version
      return data
    },
    async save () {
      if (!this.collection.name) {
        return this.showError(this._('Please enter a name for the collection'))
      }
      this.saving = true
      try {
        const data = this.getSaveData()
        let linkedGroupId = null

        if (!this.advancedCollections) {
          if (!this.editableMemberGroups.length) {
            const groupResponse = await axios.post(
              this.baseDomain + this.shopId + '/usergroups.json?apikey=' + this.apiKey,
              { name: data.name }
            )
            linkedGroupId = groupResponse.data?.id ?? groupResponse.data
            if (linkedGroupId) {
              const existing = (this.collection.sharedUsergroup || []).map(g => g.id || g)
              data.sharedUsergroup = [...new Set([...existing, linkedGroupId])]
            }
          } else if (this.editableMemberGroups.length) {
            const linkedGroup = this.editableMemberGroups[0]
            linkedGroupId = linkedGroup.id || linkedGroup
            if (this.origState) {
              const orig = JSON.parse(this.origState)
              if (orig.name !== data.name) {
                await axios.put(
                  this.baseDomain + this.shopId + '/usergroups/' + linkedGroupId + '.json?apikey=' + this.apiKey,
                  { id: linkedGroupId, name: data.name }
                )
              }
            }
          }
        }

        let response
        if (data.id) {
          response = await axios.put(
            this.baseDomain + this.shopId + '/collections/' + data.id + '.json?apikey=' + this.apiKey,
            data
          )
        } else {
          delete data.id
          response = await axios.post(
            this.baseDomain + this.shopId + '/collections.json?apikey=' + this.apiKey,
            data
          )
        }

        const collectionId = response.data?.id ?? response.data
        if (collectionId) {
          if (this.component && this.component.initData) {
            this.component.initData.collectionId = collectionId
          }
          await this.loadCollection(collectionId)
        }
        const flushGroupId = linkedGroupId || this.linkedGroupId
        if (flushGroupId) {
          await this.flushPendingInvites(flushGroupId)
        }
        this.saved = true
        this.storeState()
        this.showNotification(this._('Changes saved successfully!'))
        this.$emit('save', this.collection)
        setTimeout(() => { this.saved = false }, 1000)
      } catch (e) {
        this.showError(e)
      }
      this.saving = false
    },
    async remove () {
      if (!confirm(this._('Delete this collection?'))) return
      this.saving = true
      try {
        await axios.delete(this.baseDomain + this.shopId + '/collections/' + this.collection.id + '.json?apikey=' + this.apiKey)
        this.$emit('remove', this.collection)
        this.close()
      } catch (e) {
        this.showError(e)
      }
      this.saving = false
    }
  },
  created () {
    this.loadCategories()
    this.loadProducts()
    this.loadGroups()
    if (this.component && this.component.initData && !this.loading) {
      const collectionId = this.component.initData.collectionId
      if (collectionId) {
        this.loadCollection(collectionId)
      } else {
        this.storeState()
      }
    }
  },
  mounted () {
    this.ensureValidActiveTab()
    if (!this.origState && this.component && this.component.initData && !this.component.initData.collectionId && !this.loading) {
      this.storeState()
    }
  },
  activated () {
    this.ensureValidActiveTab()
  },
  async updated () {
    if (this.component && this.component.initData && !this.loading) {
      const initId = parseInt(this.component.initData.collectionId, 10) || null
      const currentId = this.collection && this.collection.id ? parseInt(this.collection.id, 10) : null
      if (initId === currentId) {
        return
      }
      if (initId) {
        try {
          await this.loadCollection(initId)
        } catch (e) {}
      } else {
        this.resetCollection()
      }
    }
  }
}
</script>
